/**
 * Programatic sound effects for 100 Canales Dijeron.
 * Uses Web Audio API oscillators + envelopes — zero external audio files,
 * works offline, and keeps the PWA bundle small.
 *
 * Public API: playHit(), playMiss(), playApplause(), playCountdown(), playStart(),
 *             setEnabled(bool), isEnabled(), toggleEnabled()
 */

const STORAGE_KEY = "canales_dijeron_sound";
let _ctx = null;
let _enabled = null;

const getEnabled = () => {
  if (_enabled !== null) return _enabled;
  if (typeof window === "undefined") return false;
  const raw = localStorage.getItem(STORAGE_KEY);
  _enabled = raw === null ? true : raw === "1";
  return _enabled;
};

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
};

const beep = (freq, dur, { type = "sine", gain = 0.15, attack = 0.005, decay = 0.05, when = 0 } = {}) => {
  if (!getEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + attack + dur + 0.05);
};

// Frequency-sweep helper for the buzzer / applause.
const sweep = (fromF, toF, dur, { type = "sawtooth", gain = 0.12 } = {}) => {
  if (!getEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const t0 = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(fromF, t0);
  osc.frequency.exponentialRampToValueAtTime(toF, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
};

// Family-Feud "ding" — cheerful two-tone arpeggio.
export const playHit = () => {
  beep(880, 0.18, { type: "sine", gain: 0.22 });
  beep(1320, 0.22, { type: "sine", gain: 0.2, when: 0.06 });
};

// Classic X-buzzer — harsh downward blast.
export const playMiss = () => {
  sweep(300, 90, 0.55, { type: "sawtooth", gain: 0.18 });
};

// Applause simulation: noise burst filtered + rhythmic amplitude wobble.
export const playApplause = () => {
  if (!getEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const dur = 2.4;
  const bufferSize = ctx.sampleRate * dur;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    // Pink-ish noise + wobble to imitate crowd clapping bursts.
    const wobble = 0.5 + 0.5 * Math.sin((i / ctx.sampleRate) * 18 * Math.PI);
    data[i] = (Math.random() * 2 - 1) * wobble * (1 - i / bufferSize);
  }
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 1400;
  filter.Q.value = 0.8;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.55, ctx.currentTime + 0.15);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
  src.connect(filter).connect(g).connect(ctx.destination);
  src.start();

  // Layer a triumphant chord on top.
  [523.25, 659.25, 783.99].forEach((f, i) => beep(f, 0.6, { type: "triangle", gain: 0.12, when: 0.05 + i * 0.05 }));
};

// Tick for the last-10-second countdown.
export const playCountdown = () => beep(1000, 0.08, { type: "square", gain: 0.1 });

// Buzz to signal "GO!" at the start of a round.
export const playStart = () => {
  beep(440, 0.12, { type: "square", gain: 0.15 });
  beep(880, 0.18, { type: "square", gain: 0.15, when: 0.14 });
};

// Steal-open riser — three-note ascending fanfare to signal drama.
export const playSteal = () => {
  beep(660, 0.15, { type: "triangle", gain: 0.18 });
  beep(880, 0.18, { type: "triangle", gain: 0.18, when: 0.12 });
  beep(1175, 0.25, { type: "triangle", gain: 0.22, when: 0.26 });
};

// Dramatic TV board-reveal — quick whoosh riser + bright ding chord.
export const playReveal = () => {
  sweep(220, 900, 0.22, { type: "sine", gain: 0.1 });
  beep(1046, 0.2, { type: "triangle", gain: 0.22, when: 0.16 });
  beep(1568, 0.3, { type: "triangle", gain: 0.18, when: 0.22 });
};

// Big win sting when a steal succeeds — rising sweep + triumphant arpeggio.
export const playStealWin = () => {
  sweep(400, 1200, 0.3, { type: "sawtooth", gain: 0.12 });
  [659.25, 830.61, 987.77, 1318.51].forEach((f, i) =>
    beep(f, 0.35, { type: "triangle", gain: 0.16, when: 0.1 + i * 0.06 }));
};

// Fail sting when a steal misses — deep downward blast.
export const playStealFail = () => {
  sweep(220, 70, 0.6, { type: "sawtooth", gain: 0.18 });
  beep(160, 0.4, { type: "square", gain: 0.14, when: 0.1 });
};

// ---- Suspense music bed (loops until stopped) ----
// Low drone + rhythmic heartbeat pulse, evoking a game-show "will they?" moment.
let _suspense = null;
export const startSuspense = () => {
  if (!getEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  if (_suspense) return; // already running
  const drone = ctx.createOscillator();
  const droneGain = ctx.createGain();
  drone.type = "sine";
  drone.frequency.setValueAtTime(68, ctx.currentTime);
  droneGain.gain.setValueAtTime(0.0001, ctx.currentTime);
  droneGain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.4);
  drone.connect(droneGain).connect(ctx.destination);
  drone.start();
  const heartbeat = () => {
    if (!getEnabled()) return;
    beep(150, 0.12, { type: "sine", gain: 0.13 });
    beep(150, 0.12, { type: "sine", gain: 0.1, when: 0.26 });
  };
  heartbeat();
  const interval = setInterval(heartbeat, 850);
  _suspense = { drone, droneGain, interval };
};

export const stopSuspense = () => {
  if (!_suspense) return;
  const ctx = getCtx();
  try {
    clearInterval(_suspense.interval);
    const t = ctx ? ctx.currentTime : 0;
    _suspense.droneGain.gain.cancelScheduledValues(t);
    _suspense.droneGain.gain.setValueAtTime(_suspense.droneGain.gain.value, t);
    _suspense.droneGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    _suspense.drone.stop(t + 0.35);
  } catch (_) {}
  _suspense = null;
};

// Toggle helpers exposed to UI.
export const isEnabled = () => getEnabled();
export const setEnabled = (v) => {
  _enabled = !!v;
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, _enabled ? "1" : "0");
  }
};
export const toggleEnabled = () => {
  setEnabled(!getEnabled());
  return getEnabled();
};
